import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardList, Eye, EyeOff } from 'lucide-react';
import { isFieldVisible } from '../../config/planningSchemas';
import { formatDate, formatTime, formatDateTime } from '../../lib/datetime';

/**
 * SchemaMirroredAnswers — single source of truth for displaying a SUBMITTED
 * planning form, used by BOTH the admin Workspace and the client portal.
 *
 * Layout features:
 *   • Sticky pill nav (smooth-scroll + IntersectionObserver active state)
 *   • 2-col grid (3-col on desktop) for short answers — long fields span full width
 *   • Yes/No chips instead of full label-value rows
 *   • Hide-empty toggle per section ("Show N unanswered" link)
 *   • Candle Lighting → compact 2-column table (#, For, Song)
 *   • Print-friendly via @media print (sticky nav + chrome hidden, sections un-stack)
 *   • Bigger fonts than before for legibility (label 12px, value 14px, headings 13px)
 *
 * Variant prop tweaks density (`admin` is a touch denser; `client` slightly roomier).
 */

// `admin` renders the field TITLE as a quiet caption (smaller, dimmer, lighter)
// and the entered VALUE as the primary content (brighter, a touch heavier) with
// a little more spacing between the two. `client` keeps its existing hierarchy.
const VARIANT_STYLES = {
  admin:  {
    labelSize: 'text-[10px]', valueSize: 'text-[15px]', headingSize: 'text-[13px]', sectionGap: 'space-y-3',
    labelColor: 'text-white/40', labelWeight: 'font-semibold', valueColor: 'text-white', valueWeight: 'font-medium', cellGap: 'gap-1',
    headingColor: 'text-[#E9CE7A]', hairline: true,
  },
  client: {
    labelSize: 'text-[12px]', valueSize: 'text-[14px]', headingSize: 'text-[13px]', sectionGap: 'space-y-4',
    labelColor: 'text-white/45', labelWeight: 'font-medium', valueColor: 'text-white/85', valueWeight: 'font-normal', cellGap: 'gap-0.5',
    headingColor: 'text-white/70', hairline: false,
  },
};

export default function SchemaMirroredAnswers({ schema, answers, variant = 'admin' }) {
  const a = answers || {};
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.admin;

  // ── Build per-section data once ──
  const sections = useMemo(() => {
    if (!schema?.steps) return [];
    return schema.steps.map((step) => {
      const fields = step.fields || [];
      // The song_picker is an action button, not a data field — exclude it
      // from the read-only mirror so it never shows as "— not answered".
      const visible = fields.filter((f) => f.type !== 'song_picker' && isFieldVisible(f, a));
      const filled = visible.filter((f) => hasValue(f, a[f.id]));
      const empty = visible.filter((f) => !hasValue(f, a[f.id]));
      return { step, filled, empty, total: visible.length };
    }).filter(s => s.filled.length > 0 || s.empty.length > 0);
  }, [schema, a]);

  // ── Sticky nav: track which section is in view ──
  const [activeId, setActiveId] = useState(sections[0]?.step.id || null);
  const sectionRefs = useRef({});
  useEffect(() => {
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id.replace('schema-section-', ''));
        }
      },
      { rootMargin: '-90px 0px -55% 0px', threshold: [0.05, 0.25, 0.5] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const handleJump = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // ── Fallback: unknown event type → simple humanized list ──
  if (!schema || !Array.isArray(schema.steps)) {
    const entries = Object.entries(a).filter(([, v]) => v !== '' && v !== null && v !== undefined);
    return (
      <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
          <ClipboardList className="w-3.5 h-3.5 text-[#D4AF37]/40" />
          <span className="text-[12px] font-semibold text-white/55 tracking-wide uppercase">Client Responses</span>
          <span className="text-[11px] text-white/25 ml-auto">{entries.length} fields</span>
        </div>
        <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
          {entries.map(([k, v]) => (
            <AnswerCell key={k} field={null} label={humanize(k)} value={v} styles={styles} />
          ))}
        </div>
      </div>
    );
  }

  if (!sections.length) {
    return (
      <div className="rounded-xl px-5 py-8 text-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.06)' }}>
        <p className="text-[13px] text-white/40">No planning answers yet.</p>
      </div>
    );
  }

  return (
    <div className={styles.sectionGap} data-testid="schema-mirror-root">
      {/* ── Sticky pill nav ── */}
      <div
        className="sticky top-2 z-20 print-hide"
        data-testid="schema-mirror-nav"
      >
        <div
          className="flex items-center gap-1.5 overflow-x-auto rounded-full px-2 py-1.5 backdrop-blur-md"
          style={{
            background: 'rgba(15,15,26,0.78)',
            border: '1px solid rgba(212,175,55,0.18)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
          }}
        >
          {sections.map(({ step, filled, total }) => {
            const isActive = activeId === step.id;
            return (
              <button
                key={step.id}
                onClick={() => handleJump(step.id)}
                className={`group flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-gradient-to-b from-[#E6C86A]/18 to-[#B88B1A]/12 text-[#F2D989] border border-[#D4AF37]/45'
                    : 'text-white/55 hover:text-white border border-transparent hover:border-white/10 hover:bg-white/[0.04]'
                }`}
                data-testid={`schema-mirror-nav-${step.id}`}
              >
                <span>{step.title}</span>
                <span
                  className={`text-[10px] tabular-nums ${isActive ? 'text-[#F2D989]/70' : 'text-white/30'}`}
                >
                  {filled.length}/{total}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Section cards ── */}
      <div className={styles.sectionGap}>
        {sections.map(({ step, filled, empty }) => (
          <SectionCard
            key={step.id}
            step={step}
            filled={filled}
            empty={empty}
            answers={a}
            styles={styles}
            variant={variant}
            sectionRef={(el) => { sectionRefs.current[step.id] = el; }}
          />
        ))}
      </div>

      {/* Print + grid CSS */}
      <style>{`
        @media print {
          .print-hide { display: none !important; }
          .schema-mirror-section {
            break-inside: avoid;
            background: #ffffff !important;
            border: 1px solid #d6d3d1 !important;
            color: #0f172a !important;
            page-break-inside: avoid;
          }
          .schema-mirror-section .label-text { color: #475569 !important; }
          .schema-mirror-section .value-text { color: #0f172a !important; }
          .schema-mirror-section h3, .schema-mirror-section .section-title { color: #0f172a !important; }
          .yes-chip { background: rgba(34,197,94,0.12) !important; color: #166534 !important; border-color: #86efac !important; }
          .no-chip { background: #f1f5f9 !important; color: #475569 !important; border-color: #cbd5e1 !important; }
        }
      `}</style>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* SectionCard                                                              */
/* ──────────────────────────────────────────────────────────────────────── */
function SectionCard({ step, filled, empty, answers, styles, variant = 'admin', sectionRef }) {
  const [showEmpty, setShowEmpty] = useState(false);
  const isAdmin = variant === 'admin';
  const isIntroSection = isAdmin && (step.id === 'introductions' || /introduction/i.test(step.title || ''));

  // Special case: candle lighting → render as a compact table.
  const isCandleSection =
    step.id === 'candles' ||
    /candle/i.test(step.title || '') ||
    filled.some(f => /^candle\d+/i.test(f.id));

  // Only render the candle TABLE if there are candle fields. Non-candle fields
  // in the section (like the parent yes/no toggle) still render as cells above.
  const candleFields = filled.filter(f => /^candle(\d+)(Honoree|Song)$/.test(f.id));
  const otherFilled = filled.filter(f => !/^candle\d+/.test(f.id));

  return (
    <div
      id={`schema-section-${step.id}`}
      ref={sectionRef}
      className="schema-mirror-section rounded-xl overflow-hidden scroll-mt-24"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
      data-testid={`schema-mirror-section-${step.id}`}
    >
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        <ClipboardList className="w-4 h-4 text-[#D4AF37]/55" />
        <h3 className={`section-title ${styles.headingSize} font-bold ${styles.headingColor || 'text-white/70'} tracking-wider uppercase`}>
          {step.title}
        </h3>
        <span className="ml-auto text-[11px] text-white/30 tabular-nums">
          {filled.length} of {filled.length + empty.length}
        </span>
      </div>

      <div className="px-4 py-4">
        {/* Introductions → names-left / song-right */}
        {isIntroSection && otherFilled.length > 0 && (
          <IntroTable fields={otherFilled} answers={answers} styles={styles} />
        )}
        {/* Filled non-candle fields → 2/3-col grid */}
        {!isIntroSection && otherFilled.length > 0 && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${isAdmin ? '' : 'lg:grid-cols-3'} gap-x-6 ${styles.hairline ? 'gap-y-0' : 'gap-y-3'}`}>
            {groupPairedCells(otherFilled).map((cell) =>
              cell.kind === 'pair' ? (
                <PairedCell
                  key={cell.parent.id}
                  parent={cell.parent}
                  kids={cell.kids}
                  answers={answers}
                  styles={styles}
                />
              ) : (
                <AnswerCell
                  key={cell.field.id}
                  field={cell.field}
                  label={cell.field.label || cell.field.id}
                  value={answers[cell.field.id]}
                  styles={styles}
                />
              )
            )}
          </div>
        )}

        {/* Candle Lighting compact table */}
        {isCandleSection && candleFields.length > 0 && (
          <CandleTable answers={answers} styles={styles} hadOtherFilled={otherFilled.length > 0} />
        )}

        {/* Hide-empty toggle */}
        {empty.length > 0 && (
          <div className="mt-4 pt-3 print-hide" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <button
              onClick={() => setShowEmpty((v) => !v)}
              className="inline-flex items-center gap-1.5 text-[12px] text-white/35 hover:text-white/70 transition-colors"
              data-testid={`schema-mirror-empty-toggle-${step.id}`}
            >
              {showEmpty
                ? <><EyeOff className="w-3.5 h-3.5" /> Hide {empty.length} unanswered</>
                : <><Eye className="w-3.5 h-3.5" /> Show {empty.length} unanswered</>
              }
            </button>
            {showEmpty && (
              <div className={`mt-3 grid grid-cols-1 sm:grid-cols-2 ${isAdmin ? '' : 'lg:grid-cols-3'} gap-x-6 gap-y-2 opacity-55`}>
                {empty.map((field) => (
                  <div key={field.id} className="flex flex-col gap-0.5">
                    <span className={`label-text ${styles.labelSize} text-white/30 uppercase tracking-wide font-medium`}>
                      {field.label || field.id}
                    </span>
                    <span className="value-text text-[13px] text-white/20 italic">— not answered</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* AnswerCell — single label/value cell                                     */
/* ──────────────────────────────────────────────────────────────────────── */
// Group filled fields so a Yes/No question and its SHORT follow-up answers
// (dropdowns, short text, time) render together on one line. Long answers
// (textarea / fullWidth) flow into their own full-width cell as before.
function groupPairedCells(fields) {
  const isShort = (f) => f && f.type !== 'textarea' && !f.fullWidth;
  const consumed = new Set();
  const cells = [];
  for (const f of fields) {
    if (consumed.has(f.id)) continue;
    if (f.type === 'yes_no') {
      const kids = fields.filter(
        (c) => c.conditional && c.conditional.field === f.id && isShort(c) && !consumed.has(c.id)
      );
      if (kids.length > 0) {
        kids.forEach((c) => consumed.add(c.id));
        cells.push({ kind: 'pair', parent: f, kids });
        continue;
      }
    }
    cells.push({ kind: 'single', field: f });
  }
  return cells;
}
function PairedCell({ parent, kids, answers, styles }) {
  const yes = isYes(answers[parent.id]);
  return (
    <div
      className={`flex flex-col gap-1 ${styles.hairline ? 'border-b border-white/[0.05] py-2.5' : ''}`}
      data-testid={`schema-mirror-row-${parent.id}`}
    >
      <span className={`label-text ${styles.labelSize} ${styles.labelColor} uppercase tracking-wider ${styles.labelWeight}`}>
        {parent.label || parent.id}
      </span>
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
        <YesNoChip yes={yes} />
        {kids.map((c) => (
          <span key={c.id} className="inline-flex items-baseline gap-1.5">
            <span className={`${styles.labelSize} ${styles.labelColor} uppercase tracking-wide ${styles.labelWeight}`}>{c.label || c.id}</span>
            <span className={`${styles.valueSize} ${styles.valueColor} ${styles.valueWeight} whitespace-pre-wrap`}>{format(answers[c.id], c)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
function AnswerCell({ field, label, value, styles }) {
  // Repeater (e.g. Siblings) — show each item as a sub-block
  if (field?.type === 'repeater' && Array.isArray(value)) {
    const items = value.filter((it) => it && Object.values(it).some((x) => x));
    if (!items.length) return null;
    return (
      <div className="col-span-full flex flex-col gap-1" data-testid={`schema-mirror-row-${field.id}`}>
        <span className={`label-text ${styles.labelSize} ${styles.labelColor} uppercase tracking-wide ${styles.labelWeight}`}>
          {label}
        </span>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
          {items.map((item, idx) => {
            const subRows = (field.itemFields || [])
              .map((sf) => ({ sf, v: item?.[sf.id] }))
              .filter(({ v }) => v !== undefined && v !== null && !(typeof v === 'string' && !v.trim()));
            if (!subRows.length) return null;
            return (
              <div
                key={idx}
                className="rounded-lg px-3 py-2"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.04)' }}
              >
                <div className="text-[10px] uppercase tracking-wider text-[#D4AF37]/50 mb-1">
                  {field.itemLabel || 'Item'} {idx + 1}
                </div>
                {subRows.map(({ sf, v }) => (
                  <div key={sf.id} className="flex flex-col mb-1.5 last:mb-0">
                    <span className="text-[11px] text-white/35">{sf.label || sf.id}</span>
                    <span className="value-text text-[13px] text-white">{format(v, sf)}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Yes/No chip rendering — admin keeps the chip right ON its question.
  const isYesNo = field?.type === 'yes_no' || isYesNoString(value);
  if (isYesNo) {
    const yes = isYes(value);
    return (
      <div
        className={styles.hairline ? 'flex items-center gap-2.5 border-b border-white/[0.05] py-2.5' : 'flex items-center justify-between gap-2'}
        data-testid={field?.id ? `schema-mirror-row-${field.id}` : undefined}
      >
        <span className={`label-text ${styles.labelSize} ${styles.labelColor} uppercase tracking-wider ${styles.labelWeight}`}>
          {label}
        </span>
        <YesNoChip yes={yes} />
      </div>
    );
  }

  // Long textarea / fullWidth → label on top, full-width value
  const formatted = format(value, field);
  const isLong = field?.type === 'textarea' || field?.fullWidth || (typeof formatted === 'string' && formatted.length > 60);
  if (isLong) {
    return (
      <div
        className={`col-span-full flex flex-col ${styles.cellGap}`}
        data-testid={field?.id ? `schema-mirror-row-${field.id}` : undefined}
      >
        <span className={`label-text ${styles.labelSize} ${styles.labelColor} uppercase tracking-wide ${styles.labelWeight}`}>
          {label}
        </span>
        <span className={`value-text ${styles.valueSize} ${styles.valueColor} ${styles.valueWeight} whitespace-pre-wrap leading-relaxed`}>
          {formatted}
        </span>
      </div>
    );
  }

  // Default: label on top, value below — fits 2/3-col grid cleanly
  return (
    <div
      className={`flex flex-col ${styles.cellGap} ${styles.hairline ? 'border-b border-white/[0.05] py-2.5' : ''}`}
      data-testid={field?.id ? `schema-mirror-row-${field.id}` : undefined}
    >
      <span className={`label-text ${styles.labelSize} ${styles.labelColor} uppercase tracking-wider ${styles.labelWeight} mb-0.5`}>
        {label}
      </span>
      <span className={`value-text ${styles.valueSize} ${styles.valueColor} ${styles.valueWeight} whitespace-pre-wrap leading-snug`}>
        {formatted}
      </span>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* IntroTable — names (left) / entrance song (right), one row per court      */
/* ──────────────────────────────────────────────────────────────────────── */
function IntroTable({ fields, answers, styles }) {
  const rows = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (f.type === 'song') continue;
    const next = fields[i + 1];
    const songField = next && next.type === 'song' ? next : null;
    const nameRaw = answers[f.id];
    const songRaw = songField ? answers[songField.id] : null;
    if (!hasValue(f, nameRaw) && !hasValue(songField, songRaw)) continue;
    const eyebrow = String(f.label || f.id).replace(/\s*names?$/i, '').trim() || (f.label || f.id);
    const isGoh = /goh|guest of honor|escort/i.test(f.id + ' ' + (f.label || ''));
    rows.push({ key: f.id, eyebrow, nameVal: format(nameRaw, f), songVal: songField ? format(songRaw, songField) : null, isGoh });
  }
  if (!rows.length) return null;
  return (
    <div data-testid="schema-mirror-intro-table">
      <div className="grid grid-cols-[1fr_1.15fr] gap-x-6 px-1 pb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <span className={`label-text ${styles.labelSize} ${styles.labelColor} uppercase tracking-wider font-semibold`}>Court · Names</span>
        <span className={`label-text ${styles.labelSize} ${styles.labelColor} uppercase tracking-wider font-semibold`}>Entrance song</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.key}
          className={`grid grid-cols-[1fr_1.15fr] gap-x-6 items-center py-2.5 ${r.isGoh ? 'px-2.5' : 'px-1'}`}
          style={r.isGoh
            ? { background: 'rgba(212,175,55,0.07)', borderRadius: '9px', marginTop: '6px' }
            : { borderBottom: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div>
            <div className={`text-[9.5px] font-bold uppercase tracking-wider mb-0.5 ${r.isGoh ? 'text-[#E9CE7A]' : 'text-[#D4AF37]/55'}`}>
              {r.isGoh ? `★ ${r.eyebrow}` : r.eyebrow}
            </div>
            <div className="text-[14px] font-semibold text-white leading-snug">{r.nameVal || '—'}</div>
          </div>
          <div className="text-[13.5px] text-white/85 leading-snug">
            {r.songVal ? <span>{r.songVal}</span> : <span className="text-white/25">—</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
/* ──────────────────────────────────────────────────────────────────────── */
/* CandleTable                                                              */
/* ──────────────────────────────────────────────────────────────────────── */
function CandleTable({ answers, styles, hadOtherFilled }) {
  // Discover all candle indices that have any value
  const indices = [];
  for (let i = 1; i <= 30; i++) {
    if (hasValue(null, answers[`candle${i}Honoree`]) || hasValue(null, answers[`candle${i}Song`])) {
      indices.push(i);
    }
  }
  if (!indices.length) return null;

  return (
    <div className={hadOtherFilled ? 'mt-5' : ''} data-testid="schema-mirror-candle-table">
      <div className={`label-text ${styles.labelSize} ${styles.labelColor} uppercase tracking-wide ${styles.labelWeight} mb-2`}>
        Candle Honorees
      </div>
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
        <table className="w-full text-left">
          <thead>
            <tr style={{ background: 'rgba(212,175,55,0.06)' }}>
              <th className="px-3 py-2 text-[11px] font-semibold text-[#D4AF37]/70 uppercase tracking-wider w-14">#</th>
              <th className="px-3 py-2 text-[11px] font-semibold text-[#D4AF37]/70 uppercase tracking-wider">Honoree / For</th>
              <th className="px-3 py-2 text-[11px] font-semibold text-[#D4AF37]/70 uppercase tracking-wider">Song</th>
            </tr>
          </thead>
          <tbody>
            {indices.map((i) => (
              <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <td className={`px-3 py-2 ${styles.valueSize} font-semibold text-[#D4AF37]/80 tabular-nums`}>{i}</td>
                <td className={`value-text px-3 py-2 ${styles.valueSize} text-white/85`}>{format(answers[`candle${i}Honoree`]) || '—'}</td>
                <td className={`value-text px-3 py-2 ${styles.valueSize} text-white/85`}>{format(answers[`candle${i}Song`]) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* YesNoChip                                                                */
/* ──────────────────────────────────────────────────────────────────────── */
function YesNoChip({ yes }) {
  if (yes) {
    return (
      <span
        className="yes-chip inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
        style={{ background: 'rgba(34,197,94,0.12)', color: '#86efac', border: '1px solid rgba(34,197,94,0.3)' }}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
        Yes
      </span>
    );
  }
  return (
    <span
      className="no-chip inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide"
      style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.25)' }} />
      No
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                  */
/* ──────────────────────────────────────────────────────────────────────── */
function hasValue(field, v) {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string' && !v.trim()) return false;
  if (Array.isArray(v)) {
    if (!v.length) return false;
    if (field?.type === 'repeater') {
      return v.some((item) => item && Object.values(item).some((x) => x !== '' && x !== null && x !== undefined));
    }
    return true;
  }
  if (typeof v === 'object') return Object.values(v).some((x) => x !== '' && x !== null && x !== undefined);
  return true;
}

function isYesNoString(v) {
  if (typeof v !== 'string') return false;
  const s = v.trim().toLowerCase();
  return s === 'yes' || s === 'no';
}

function isYes(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.trim().toLowerCase() === 'yes';
  return false;
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const WALL_TIME_RE = /^\d{1,2}:\d{2}(?::\d{2})?$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function format(v, field) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    if (v.length === 0) return '';
    if (v.every((x) => typeof x !== 'object')) return v.filter((x) => x !== '' && x !== null).join(', ');
    return v.map((o) => Object.values(o).filter(Boolean).join(' — ')).join('; ');
  }
  if (typeof v === 'object') {
    return Object.entries(v)
      .filter(([, x]) => x !== '' && x !== null && x !== undefined)
      .map(([k, x]) => `${humanize(k)}: ${x}`)
      .join(' · ');
  }
  const s = String(v).trim();
  // Date/time answers render like the rest of the app — dates as MM/DD/YYYY,
  // times as 12-hour AM/PM — reusing the app's formatters, which format
  // date-only and wall-clock values as-entered (NO timezone/UTC conversion).
  // Field type is the primary signal; the value-pattern fallback catches every
  // date/time answer even where the field type isn't threaded through.
  if (field?.type === 'date') return formatDate(s) || s;
  if (field?.type === 'time') return formatTime(s) || s;
  // Pattern fallback ONLY where the field type isn't known (fallback list,
  // candle table) — so a typed text/song answer is never misread as a time.
  if (!field) {
    if (DATE_ONLY_RE.test(s)) return formatDate(s) || s;
    if (ISO_DATETIME_RE.test(s)) return formatDateTime(s) || s;
    if (WALL_TIME_RE.test(s)) return formatTime(s) || s;
  }
  if (s.toLowerCase() === 'yes') return 'Yes';
  if (s.toLowerCase() === 'no') return 'No';
  return s;
}

function humanize(k) {
  return String(k)
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}
